import type { ActionTestResult } from "@mailhand/core/api";
import type { WebhookAction } from "@mailhand/core/schemas";
import { useEffect, useState } from "react";
import { useApi } from "../../app/services.js";
import { errorMessage } from "../../lib/useResource.js";
import { Badge, CopyButton, Drawer, ErrorNote, Eyebrow, Spinner } from "../../ui/index.js";

type TestState = { status: "idle" } | { status: "running" } | { status: "error"; message: string } | { status: "done"; result: ActionTestResult };

const SUCCESS_MIN = 200;
const SUCCESS_MAX = 299;

function prettyBody(body: string): string {
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
}

/** Sends the sample payload once each time the drawer opens, and shows both sides. */
export function TestDrawer({ open, onClose, action }: { open: boolean; onClose: () => void; action: WebhookAction }) {
  const api = useApi();
  const [state, setState] = useState<TestState>({ status: "idle" });

  useEffect(() => {
    if (!open) return;
    let current = true;
    setState({ status: "running" });
    api
      .call("actionTest", { params: { id: action.id }, body: {} })
      .then((result) => {
        if (current) setState({ status: "done", result });
      })
      .catch((error: unknown) => {
        if (current) setState({ status: "error", message: errorMessage(error) });
      });
    return () => {
      current = false;
    };
  }, [open, api, action.id]);

  return (
    <Drawer open={open} onClose={onClose} title={`Test "${action.name}"`}>
      {state.status === "running" ? (
        <div className="drawer-wait">
          <Spinner /> <span className="muted">Calling {action.webhook.url}</span>
        </div>
      ) : null}
      {state.status === "error" ? <ErrorNote message={state.message} /> : null}
      {state.status === "done" ? <TestResult result={state.result} /> : null}
    </Drawer>
  );
}

function TestResult({ result }: { result: ActionTestResult }) {
  const { status, body, latencyMs, outcome } = result.response;
  const ok = status !== null && status >= SUCCESS_MIN && status <= SUCCESS_MAX;
  const requestJson = JSON.stringify(result.request, null, 2);
  return (
    <div className="stack-lg">
      <section className="stack">
        <Eyebrow>Response</Eyebrow>
        <div className="test-summary">
          <Badge tone={ok ? "green" : "orange"} dot>
            {status === null ? "No answer" : `HTTP ${status}`}
          </Badge>
          <span className="mono">
            {latencyMs}
            <span className="unit"> ms</span>
          </span>
          <span className="muted small">{outcome}</span>
        </div>
        <pre className="code">{body === "" ? "(empty body)" : prettyBody(body)}</pre>
      </section>
      <section className="stack">
        <div className="section-row">
          <Eyebrow>Signature</Eyebrow>
          <CopyButton value={result.signature} />
        </div>
        <pre className="code code-wrap">{result.signature}</pre>
        <p className="muted small">Your endpoint should compute the same value from the timestamp, the raw body, and the secret.</p>
      </section>
      <section className="stack">
        <div className="section-row">
          <Eyebrow>Request body</Eyebrow>
          <CopyButton value={requestJson} />
        </div>
        <pre className="code">{requestJson}</pre>
      </section>
    </div>
  );
}
